import { useCallback } from 'react';
import { useStorage } from '../../../context/storage-context';
import type { StorageKeyBuilder } from '../../../context/use-storage-key';
import type {
  ChecklistItemStatus,
  EffectiveChecklistItemStatus,
  UseChecklistItemStatus,
} from '../types';

export type StepStatus = {
  /** The status hook's result folded with the persisted skip flag. `undefined` while loading. */
  status: EffectiveChecklistItemStatus | undefined;
  isSkipped: boolean;
  skip: () => void;
  unskip: () => void;
};

/**
 * Combines a step's injected status hook with its persisted skip flag into one effective status.
 * Shared by `Checklist.Item` and the headless `StatusProbe` so both read the same skip state (the
 * flag is reactive across `useStorage` instances) and resolve identically.
 *
 * @param storageKeyBuilder Namespaces the persisted skip flag (`storageKeyBuilder(id, 'skipped')`);
 *   without one it falls back to a local, in-session key (Storybook/tests).
 */
export function useStepStatus(
  id: string,
  useStatus: UseChecklistItemStatus,
  storageKeyBuilder?: StorageKeyBuilder,
): StepStatus {
  const raw = useStatus();
  const key = storageKeyBuilder
    ? storageKeyBuilder(id, 'skipped')
    : `checklist:ephemeral:${id}:skipped`;
  const [persistedSkip, setSkip, clearSkip] = useStorage<boolean>(key);
  const isSkipped = persistedSkip === true;

  const skip = useCallback(() => setSkip(true), [setSkip]);
  const unskip = useCallback(() => clearSkip(), [clearSkip]);

  return { status: resolveEffectiveStatus(raw, isSkipped), isSkipped, skip, unskip };
}

/**
 * Folds a step's raw status with its skip flag. Real completion wins over a skip — a step that's
 * later genuinely done loses the `skipped` clock — and `undefined` (loading) passes through.
 */
function resolveEffectiveStatus(
  raw: ChecklistItemStatus | undefined,
  isSkipped: boolean,
): EffectiveChecklistItemStatus | undefined {
  if (raw === undefined) return undefined;
  if (raw === 'complete') return 'complete';
  if (isSkipped) return 'skipped';
  return 'pending';
}
